import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  Input,
  OnInit,
} from '@angular/core';
import { Component as BrComponent, Page } from '@bloomreach/spa-sdk/';
import { AnalyticsService } from '@frk/eds-components';
import { WINDOW } from '@ng-web-apis/common';
import { BaseProductDetailComponent } from '@products/base-product-detail.component';
import { EMDASH } from '@products/utils/constants/product.constants';
import {
  assetStatistics,
  ConfigurationId,
  FundId,
  FundUmbrella,
  LiteratureDocument,
  PortfolioDisplay,
  positionStatistics,
  ProductDetailConfiguration,
  ProductType,
  Statistic,
} from '@types';
import { Logger } from '@utils/logger';
import { take } from 'rxjs/operators';
import { PortfolioService } from '../services/portfolio.service';
import { StatisticRow } from './benchmark-table/benchmark-table.component';

const logger = Logger.getLogger('StatisticsAndPositionsComponent');

export interface StatisticsAndPositionsData {
  productType?: ProductType;
  fundUmbrella?: FundUmbrella;
  statistics: Statistic[];
}

export interface Benchmark {
  id: string;
  name: string;
}

export interface Frequency {
  asOfDate: string;
  updateFrequency: string;
}

@Component({
  selector: 'ft-statistics-and-positions',
  templateUrl: './statistics-and-positions.component.html',
  styleUrls: ['./statistics-and-positions.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StatisticsAndPositionsComponent
  extends BaseProductDetailComponent
  implements OnInit, PortfolioDisplay {
  @Input() data: StatisticsAndPositionsData;
  @Input() page!: Page;
  @Input() component!: BrComponent;
  public fundId: FundId;

  public assets: Statistic[] = [];
  public assetsRows: StatisticRow[] = [];
  public assetsBenchmark: Benchmark;
  public assetsFreq: Frequency;

  public positions: Statistic[] = [];
  public positionsRows: StatisticRow[] = [];
  public positionsBenchmark: Benchmark;
  public positionsFreq: Frequency;

  public mainStatistics: Statistic[] = [];
  public mainRows: StatisticRow[] = [];
  public mainBenchmark: Benchmark;
  public mainFreq: Frequency;

  public isPopulated = false;
  public isDocumentAvailable = false;
  public showPositionsPanel = false;

  private config: ProductDetailConfiguration;
  private documentLink: string;
  private documentData: LiteratureDocument;

  constructor(
    private analytics: AnalyticsService,
    private portfolioService: PortfolioService,
    private cdr: ChangeDetectorRef,
    @Inject(WINDOW) readonly windowRef: Window
  ) {
    super();
  }

  ngOnInit(): void {
    this.setConfig();
  }

  private setConfig(): void {
    this.config = this.component?.getModels()?.config;
    this.fundId = this.config?.fundId;
  }

  /**
   * When data is passed into this component, this function checks if it can be displayed
   * Called direct from Entry component, as ngOnInit and ngOnchanges not called when dynamic component
   */
  public processData(): void {
    this.setConfig();
    this.isDocumentAvailable = false;
    logger.debug('processData()', this.data);
    this.isPopulated =
      this.data && this.data.statistics && this.data.statistics.length > 0;
    this.data.statistics.forEach((stat: Statistic): void => {
      if (stat.tooltip) {
        stat = { ...stat, tooltipName: stat.label };
      }
      if (
        assetStatistics.some((assetId: string): boolean => stat.id === assetId)
      ) {
        this.assets.push(stat);
      } else if (
        positionStatistics.some(
          (positionId: string): boolean => stat.id === positionId
        )
      ) {
        this.positions.push(stat);
      } else {
        this.mainStatistics.push(stat);
      }
    });
    this.assets = this.orderAssetData(this.assets);

    this.setDownloadUrl();
    logger.debug('assetStatistics', assetStatistics);
    logger.debug('positionStatistics', positionStatistics);
    logger.debug('assets', this.assets);
    logger.debug('positions', this.positions);
    logger.debug('mainStatistics', this.mainStatistics);
    logger.debug(
      'overall asOfDate',
      this.calcMainAsOfDate(this.data.statistics)
    );
    logger.debug('assets asOfDate', this.calcMainAsOfDate(this.assets));
    logger.debug('positions asOfDate', this.calcMainAsOfDate(this.positions));
    logger.debug(
      'mainStatistics asOfDate',
      this.calcMainAsOfDate(this.mainStatistics)
    );

    this.assetsBenchmark = this.getPrimaryBenchmark(this.assets);
    this.assetsFreq = this.calcMainAsOfDate(this.assets);
    if (this.assetsFreq) {
      this.assets = this.removeFrequency(this.assets, this.assetsFreq);
      this.assetsRows = this.getStatisticRows(this.assets);
    }
    logger.debug('assetsBenchmark', this.assetsBenchmark);

    this.positionsBenchmark = this.getPrimaryBenchmark(this.positions);
    this.positionsFreq = this.calcMainAsOfDate(this.positions);
    if (this.positionsFreq) {
      this.positions = this.removeFrequency(this.positions, this.positionsFreq);
      this.positionsRows = this.getStatisticRows(this.positions);
    }
    logger.debug('positionsBenchmark', this.positionsBenchmark);

    this.mainBenchmark = this.getPrimaryBenchmark(this.mainStatistics);
    this.mainFreq = this.calcMainAsOfDate(this.mainStatistics);
    if (this.mainFreq) {
      this.mainStatistics = this.removeFrequency(
        this.mainStatistics,
        this.mainFreq
      );
      this.mainRows = this.getStatisticRows(this.mainStatistics);
    }
    logger.debug('mainBenchmark', this.mainBenchmark);
    this.cdr.detectChanges();
  }

  // custom sort for assest
  private orderAssetData(assets: Statistic[]): Statistic[] {
    if (this.data?.productType === ConfigurationId.CEF) {
      const arrangedAssetData: Statistic[] = [];
      assetStatistics.forEach((id) => {
        assets.forEach((assetData) => {
          if (assetData.id === id) {
            arrangedAssetData.push(assetData);
          }
        });
      });
      return arrangedAssetData;
    } else {
      return assets;
    }
  }

  // this turns Statistic[] into StatisticRow{}, so it can be used in the BM table component
  private getStatisticRows = (stats: Statistic[]): StatisticRow[] => {
    const valStats: Statistic[] = stats.filter(
      (stat: Statistic): boolean => !stat.mainStatisticId
    );
    const bmStats: Statistic[] = stats.filter(
      (stat: Statistic): boolean => !!stat.mainStatisticId
    );
    return valStats.map(
      (stat: Statistic): StatisticRow => ({
        label: stat.label,
        val: stat.val,
        bmVal:
          bmStats.find(
            (bmStat: Statistic): boolean => bmStat.mainStatisticId === stat.id
          )?.val || EMDASH,
        caveatPlacement: stat.caveatPlacement,
        secondaryCaveatPlacement: stat.secondaryCaveatPlacement,
        tooltip: stat.tooltip,
        asOfLabel: stat.asOfLabel,
        asOfDate: stat.asOfDate,
        updateFrequency: stat.updateFrequency,
      })
    );
  };

  // gets the main BM for an array of Statistics
  // For now, just returns first BM found, as we don't have exmaple data of multiple BMs
  private getPrimaryBenchmark = (stats: Statistic[]): Benchmark => {
    // BM stats are defined by having mainStatisticId set
    const bmStat: Statistic = stats.find(
      (stat: Statistic): boolean => !!stat.mainStatisticId
    );
    if (bmStat) {
      return {
        id: bmStat.benchmarkId,
        name: bmStat.benchmarkName,
      };
    }
    // if no BM found, return null
    return null;
  };

  // this returns the most common asOfDate in stats array
  private calcMainAsOfDate = (stats: Statistic[]): Frequency =>
    stats.reduce(
      (
        mainDate: Frequency,
        stat: Statistic,
        index: number,
        allStats: Statistic[]
      ) => {
        const newDate: Frequency = {
          asOfDate: stat.asOfDate,
          updateFrequency: stat.updateFrequency,
        };
        return mainDate &&
          allStats.filter(
            (s: Statistic): boolean =>
              s.updateFrequency === mainDate.updateFrequency
          ).length >
            allStats.filter(
              (s: Statistic): boolean =>
                s.updateFrequency === newDate.updateFrequency
            ).length
          ? mainDate
          : newDate;
      },
      null
    );

  // this removes the asOfDate and updateFrequency props when they match the main asOfDate
  // because the main asOfDate is now shown in the panel header
  private removeFrequency = (
    stats: Statistic[],
    freq: Frequency
  ): Statistic[] =>
    stats.map(
      (stat: Statistic): Statistic => ({
        ...stat,
        asOfDate: stat.asOfDate === freq.asOfDate ? null : stat.asOfDate,
        updateFrequency:
          stat.updateFrequency === freq.updateFrequency
            ? null
            : stat.updateFrequency,
      })
    );

  private setDownloadUrl() {
    this.portfolioService
      .getDownloadUrl(
        this.config?.fundId,
        this.config?.shareClassCode,
        this.data.fundUmbrella
      )
      .pipe(take(1))
      .subscribe((documentData: LiteratureDocument) => {
        if (documentData.pdfDownloadLink) {
          this.documentLink = documentData.pdfDownloadLink;
          this.documentData = documentData;
          this.isDocumentAvailable = true;
        }
        this.showPositionsPanel = this.getShowPositionsPanel();
        this.cdr.detectChanges();
      });
  }

  private getShowPositionsPanel(): boolean {
    let isPositionsDataAvailable = this.positions?.length > 0;
    if (isPositionsDataAvailable && this.positionsBenchmark) {
      // NGC-14398: Hide Positions section , when we don't have data in place.
      isPositionsDataAvailable = this.positionsRows?.length > 0;
    }
    return isPositionsDataAvailable || this.isDocumentAvailable;
  }

  public openDocument() {
    this.analytics.trackEvent({
      event: 'file_download',
      detailed_event: 'Download Link Clicked',
      action: 'download pdf',
      file_name: this.documentData.dctermsTitle,
      file_lit_code: this.documentData.docId,
      event_data: {
        file_extension: 'pdf',
        file_lit_code: this.documentData.docId,
        file_name: this.documentData.dctermsTitle,
        link_url: this.documentLink,
        location: '',
      },
    });
    this.windowRef.open(this.documentLink, '_blank');
  }
}
